import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoaderService } from '@app/main/cablepro/loader.service';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { TrialBannerComponent } from '@app/shared/layout/trial-banner/trial-banner.component';
import { CommonModule } from '@node_modules/@angular/common';
import { NavigationEnd, Router } from '@node_modules/@angular/router';
import { NgbModal, NgbModalConfig } from '@node_modules/@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@node_modules/@ng-select/ng-select';
import { Subscription } from '@node_modules/rxjs';
import { filter } from '@node_modules/rxjs/operators';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase, RequireLogin } from '@shared/common/app-component-base';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { UserLoginInfoDto } from '@shared/service-proxies/service-proxies';
import { SharedService } from '@shared/service-proxies/shared.service';
import { FeatureCode } from '@shared/utils/feature-code';
import { LoginService } from 'account/login/login.service';
import { WebfacingLoginComponent } from 'webfacing/webfacing-login/webfacing-login.component';

@Component({
  selector: 'app-webfacing-header',
  templateUrl: './webfacing-header.component.html',
  styleUrls: ['./webfacing-header.component.scss'],
  standalone: true,
  imports: [CommonModule, NgSelectModule]
})
export class WebfacingHeaderComponent extends AppComponentBase implements OnInit, OnDestroy {
  private userSubscription: Subscription;
  title = 'cable-sizing';
  showReportButton: boolean = false;
  selectedCalculator = 1;
  selectedCalculatorName: string = null;
  redirectUrl: string = "";
  public user!: UserLoginInfoDto | undefined;
  public isAdminUser: boolean = false;
  public isAnnualProfessionalPlan: boolean = false;

  @ViewChild('iframeElement') iframeElement!: ElementRef<HTMLIFrameElement>;
  public calculators = [
    { "name": "Select Calculator", "value": 1, url: undefined },
    { "name": "AS 3008 Cable Sizing Calculator", "value": 2, url: 'cable-sizing-as' },
    { "name": "BS 7671 Cable Sizing Calculator", "value": 4, url: 'cable-sizing-bs' },
    { "name": "NZ 3008 Cable Sizing Calculator", "value": 3, url: 'cable-sizing-nz' },
    { "name": "IEC 60364-5-52 Cable Sizing", "value": 5, url: 'cable-sizing-iec' },
    { "name": "NFPA (70) NEC Cable Sizing", "value": 13, url: 'cable-sizing-nec' },
    { "name": "Maximum Demand Calculator", "value": 6, url: 'max-demand' },
    { "name": "LV Network Calculator", "value": 7, url: 'https://cablepro.elek.com/create-free-account', external: true },
    { "name": "Protection Coordination Calculator", "value": 8, url: 'protection-coordination' },
    { "name": "Cable Pulling Calculator", "value": 9, url: 'cable-pulling' },
    { "name": "Arc Flash Calculator", "value": 10, url: 'arc-flash' },
  ];

  public calculatorMappings = [
    { path: '/cable-sizing-as', id: 2 },
    { path: '/cable-sizing-nz', id: 3 },
    { path: '/cable-sizing-bs', id: 4 },
    { path: '/cable-sizing-iec', id: 5 },
    { path: '/cable-sizing-nec', id: 13 },
    { path: '/max-demand', id: 6 },
    { path: '/protection-coordination', id: 8 },
    { path: '/cable-pulling', id: 9 },
    { path: '/arc-flash', id: 10 }
  ];

  public _router: Router;
  public FeatureCode = FeatureCode;
  isReportDisabled = true;
  private sub!: Subscription;

  constructor(injector: Injector, private sharedService: SharedService, private _appSessionService: AppSessionService, public loginService: LoginService, private loaderService: LoaderService,
    private _authService: AppAuthService, private readonly router: Router, private modalService: NgbModal) {
    super(injector);
    (window as any).wf_header = this;
    this.user = this._appSessionService.user;
    if (this.user) {
      this.isAdminUser = (this.user.id === 1 || this.user.id === 2);
    }
    if (this.user?.premiumUser) {
      let IsUserHasProfessionalPlan = this.user?.userStripePlanLevel == 3;
      this.isAnnualProfessionalPlan = IsUserHasProfessionalPlan && this.user?.userStripePlanInterval.toLowerCase().trim() === 'year';
    }
    this._router = router;
  }

  ngOnInit() {
    this.selectedCalculatorName = 'Select Calculator';

    this.setRedirecUrl(this.router.url);
    this.showReportButton = this.isReportButtonDisplay(this.router.url);
    this.setCalculatorDropDown(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.showReportButton = this.isReportButtonDisplay(event.url);
        this.setCalculatorDropDown(event.url);
        this.setRedirecUrl(this.router.url);
      });

    this.sub = this.loaderService.reportDisabled$
      .subscribe(value => {
        this.isReportDisabled = value;
      });
  }

  setRedirecUrl(url) {
    const match = this.calculatorMappings.find(mapping => url.includes(mapping.path));
    if (match) {
      this.redirectUrl = "/calculators";
    } else {
      this.redirectUrl = "/";
    }
  }

  setCalculatorDropDown(url: string) {
    const match = this.calculatorMappings.find(mapping => url.includes(mapping.path));
    this.selectedCalculator = match ? match.id : 1;
  }

  isReportButtonDisplay(url: string): boolean {
    const reportUrls = ['/cable-sizing-as', '/cable-sizing-bs', '/cable-sizing-nz', '/cable-sizing-nec', '/cable-pulling', '/arc-flash', '/max-demand', '/protection-coordination', '/cable-sizing-iec'];
    return reportUrls.some(route => url.includes(route));
  }

  redirectToFullAppUrl() {
    let asNzUrl = "https://elek.com/electrical-software/elek-cable-pro-web/";
    let bsUrl = "https://elek.com/electrical-software/elek-cable-pro-web-bs7671/";
    let redirectUrl = "";

    let url = this.router.url;
    if (url.includes('/cable-sizing-as') || url.includes('/cable-sizing-nz')) {
      redirectUrl = asNzUrl;
    } else if (url.includes('/cable-sizing-bs')) {
      redirectUrl = bsUrl;
    } else {
      redirectUrl = asNzUrl;
    }

    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  }

  @RequireLogin()
  onReportClick() {
    this.sharedService.triggerAction('report');
  }

  onCalculationStep() {
    if (this._appSessionService.hasPermission(this.FeatureCode.FDCSCSIS)) {
      this.sharedService.triggerAction('calculationstep');
    }
    else {
      this._appSessionService.showUpgradeAlert();
    }
  }

  onCalculationChange(event: any) {
    if (event.url === undefined || event.disabled == true) return;
    if (event.external) {
      window.open(event.url, '_blank', 'noopener,noreferrer');
    } else if (event.url) {
      this.selectedCalculator = event.value;
      if (location.hostname === "localhost") {
        location.href = `${event.url}`;
      }
      else {
        location.href = `/calculators/${event.url}`;
      }
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.userSubscription)
      this.userSubscription.unsubscribe();

    this.sub?.unsubscribe();
  }

  onLogin() {
    this.modalService.open(WebfacingLoginComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      animation: true
    });
  }

  logout(): void {
    if (localStorage.getItem("ReportSettingEmails"))
      localStorage.removeItem("ReportSettingEmails");

    this._authService.logout(false);
    if (window.location.hostname === "elek.com") {
      location.href = "/calculators";
    }
    else {
      location.href = "/";
    }
  }

  onAllProjectsClick() {
    window.location.href = AppConsts.appBaseUrl;
  }

  toggleMobileMenu() {
    $('body').toggleClass('m-topbar--on');
  }

  UpgradePlan() {
    if (window.location.hostname === "localhost") {
      location.href = "/pricing";
    }
    else {
      location.href = "/calculators/pricing";
    }
  }
}